import React, {
  type ComponentType,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { useClinicians } from '@features/organizations';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { ProviderBadge } from '@jotpsych/ui';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import RepeatOutlinedIcon from '@mui/icons-material/RepeatOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Stack,
  type SvgIconProps,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import {
  add,
  differenceInCalendarDays,
  differenceInMinutes,
  format,
  isValid,
  parseISO,
} from 'date-fns';
import { useSnackbar } from 'notistack';

import { useRecurrence } from '../hooks/useRecurrence';
import {
  useCreateBlockedTimeRuleMutation,
  useDeleteBlockedTimeRuleMutation,
  useUpdateBlockedTimeRuleMutation,
} from '../store/blockedTimeApi';
import type { Appointment } from '../types';
import type { BlockedTimeOccurrence, BlockedTimeScope } from '../types/blockedTime';
import {
  blockedTimeOverlaps,
  buildBlockedTimeDraft,
  coercePositiveDurationMinutes,
  getBlockedTimeDateSpan,
  getBrowserTimeZone,
  toCreateAvailabilityRuleRequest,
  toDateInputValue,
  toTimeInputValue,
  toUpdateAvailabilityRuleRequest,
} from '../utils/blockedTime';

import CustomRecurrenceModal from './CustomRecurrenceModal';

type BlockedTimeFormMode = 'create' | 'edit';

interface BlockedTimeFormProps {
  mode: BlockedTimeFormMode;
  blockedTime?: BlockedTimeOccurrence | null;
  initialScheduledTime?: Date;
  initialDuration?: number;
  initialProviderId?: string;
  initialAllDay?: boolean;
  isOrgScheduleActive: boolean;
  /**
   * Provider to preselect when the block has no explicit provider. Callers
   * pass the current user only when they are themselves a clinician
   * (GDN-2225); non-clinicians must pick a provider.
   */
  defaultProviderId?: string;
  appointments: Appointment[];
  onClose: () => void;
  onSaved?: () => void;
  onDeleted?: () => void;
  hideActions?: boolean;
  onActionStateChange?: (state: BlockedTimeActionState) => void;
}

export interface BlockedTimeActionState {
  isEditing: boolean;
  isSubmitting: boolean;
}

export interface BlockedTimeFormHandle {
  remove: () => void;
  save: () => void;
}

const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom...' },
];

const DURATION_PRESETS = [30, 45, 60, 90, 120] as const;

export const BlockedTimeForm = forwardRef<BlockedTimeFormHandle, BlockedTimeFormProps>(
  (
    {
      mode,
      blockedTime,
      initialScheduledTime,
      initialDuration,
      initialProviderId,
      initialAllDay = false,
      isOrgScheduleActive,
      defaultProviderId,
      appointments,
      onClose,
      onSaved,
      onDeleted,
      hideActions = false,
      onActionStateChange,
    },
    ref,
  ) => {
    const { enqueueSnackbar } = useSnackbar();
    const { blockedTime: blockedTimeTracking } = useEventTracking();
    const { clinicians, isLoading: isLoadingClinicians } = useClinicians({
      skip: !isOrgScheduleActive,
    });
    const recurrence = useRecurrence();
    const [createBlockedTimeRule, createState] = useCreateBlockedTimeRuleMutation();
    const [updateBlockedTimeRule, updateState] = useUpdateBlockedTimeRuleMutation();
    const [deleteBlockedTimeRule, deleteState] = useDeleteBlockedTimeRuleMutation();

    const [scope, setScope] = useState<BlockedTimeScope>('self');
    const [providerId, setProviderId] = useState<string | undefined>(initialProviderId);
    const [date, setDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [allDay, setAllDay] = useState(false);
    const [timezone, setTimezone] = useState(getBrowserTimeZone());
    const [error, setError] = useState<string | null>(null);

    const isSubmitting = createState.isLoading || updateState.isLoading || deleteState.isLoading;
    const isEditing = Boolean(mode === 'edit' && blockedTime?.ruleId);

    useEffect(() => {
      if (blockedTime) {
        const ruleStart = new Date(blockedTime.rule.start_time);
        const ruleEnd = new Date(ruleStart.getTime() + blockedTime.rule.duration_minutes * 60_000);
        const dateSpan = getBlockedTimeDateSpan(blockedTime.rule);
        const ruleTimezone = blockedTime.rule.timezone || getBrowserTimeZone();

        setScope(
          blockedTime.rule.provider_id == null
            ? 'organization'
            : isOrgScheduleActive
              ? 'provider'
              : 'self',
        );
        setProviderId(blockedTime.rule.provider_id ?? initialProviderId ?? defaultProviderId);
        setDate(dateSpan.date);
        setEndDate(dateSpan.endDate);
        setStartTime(toTimeInputValue(ruleStart, ruleTimezone));
        setEndTime(toTimeInputValue(ruleEnd, ruleTimezone));
        setAllDay(blockedTime.allDay);
        setTimezone(ruleTimezone);
        setError(null);

        if (blockedTime.rule.rrule) {
          recurrence.parseRRule(blockedTime.rule.rrule);
        } else {
          recurrence.setRecurrence('none');
          recurrence.setRecurrenceEndDate('');
          recurrence.setHasRecurrenceEnd(false);
          recurrence.setCustomRecurrenceRule(null);
        }
        return;
      }

      const start = initialScheduledTime ?? new Date();
      const duration = initialDuration ?? 60;
      const end = add(start, { minutes: duration });
      const displayEndDate = initialAllDay ? add(end, { minutes: -1 }) : end;

      setScope(isOrgScheduleActive ? 'provider' : 'self');
      setProviderId(initialProviderId ?? defaultProviderId);
      setDate(toDateInputValue(start));
      setEndDate(toDateInputValue(displayEndDate));
      setStartTime(toTimeInputValue(start));
      setEndTime(toTimeInputValue(end));
      setAllDay(initialAllDay);
      setTimezone(getBrowserTimeZone());
      setError(null);
      recurrence.setRecurrence('none');
      recurrence.setRecurrenceEndDate('');
      recurrence.setHasRecurrenceEnd(false);
      recurrence.setCustomRecurrenceRule(null);
      // Only reset the form when the edited block or initial slot changes.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      blockedTime,
      defaultProviderId,
      initialDuration,
      initialAllDay,
      initialProviderId,
      initialScheduledTime,
      isOrgScheduleActive,
    ]);

    const recurrenceData = recurrence.generateRRule();

    const draft = useMemo(
      () =>
        buildBlockedTimeDraft({
          scope,
          providerId,
          date,
          endDate,
          startTime,
          endTime,
          allDay,
          timezone,
          rrule: recurrenceData?.rrule ?? null,
          recurrenceEndDate: recurrenceData?.endDate ?? null,
        }),
      [allDay, date, endDate, endTime, providerId, recurrenceData, scope, startTime, timezone],
    );

    const overlapsAppointment = useMemo(() => {
      if (!draft) return false;

      const intervals = appointments.flatMap(appointment => {
        if (!appointment.start_time) return [];
        const appointmentProviderId = appointment.provider_id ?? null;
        const appliesToAppointment =
          scope === 'organization' ||
          (scope === 'provider' && appointmentProviderId === providerId) ||
          (scope === 'self' && (!providerId || appointmentProviderId === providerId));

        if (!appliesToAppointment) return [];

        const start = new Date(appointment.start_time);
        const end = add(start, { minutes: appointment.duration || 30 });
        return [{ start, end }];
      });

      return blockedTimeOverlaps(draft, intervals);
    }, [appointments, draft, providerId, scope]);

    const providerName =
      clinicians.find(clinician => clinician.userId === providerId)?.name ??
      (providerId ? 'Assigned provider' : null);
    const dateTimeLabel = formatBlockedTimeStartDateTime({
      allDay,
      date,
      startTime,
    });
    const durationLabel = formatBlockedTimeDuration({ allDay, date, endDate, draft });
    const recurrenceLabel = getBlockedTimeRecurrenceLabel({
      recurrence: recurrence.recurrence,
      recurrenceText: recurrence.getRecurrenceText(),
      recurrenceEndDate: recurrence.recurrenceEndDate,
    });

    const handleStartDateChange = (nextDate: string) => {
      if (allDay) {
        const spanDays = getInclusiveDateSpanDays(date, endDate);
        setDate(nextDate);
        setEndDate(addDaysToDateInput(nextDate, spanDays - 1));
        return;
      }

      const durationMinutes = getTimedDurationMinutes(date, startTime, endDate, endTime);
      setDate(nextDate);
      setEndFromDuration(nextDate, startTime, durationMinutes, setEndDate, setEndTime);
    };

    const handleStartTimeChange = (nextStartTime: string) => {
      const durationMinutes = getTimedDurationMinutes(date, startTime, endDate, endTime);
      setStartTime(nextStartTime);
      setEndFromDuration(date, nextStartTime, durationMinutes, setEndDate, setEndTime);
    };

    const handleTimedDurationChange = (durationMinutes: number) => {
      setEndFromDuration(date, startTime, durationMinutes, setEndDate, setEndTime);
    };

    const validate = useCallback((): string | null => {
      if (!draft) return 'Choose a valid time range.';
      if (isOrgScheduleActive && scope === 'provider' && !providerId) {
        return 'Choose a provider for this block.';
      }
      return null;
    }, [draft, isOrgScheduleActive, providerId, scope]);

    const handleSave = useCallback(async () => {
      const validationError = validate();
      if (validationError || !draft) {
        setError(validationError);
        return;
      }

      setError(null);

      try {
        const durationMinutes = coercePositiveDurationMinutes(draft.start, draft.end);
        if (isEditing && blockedTime?.ruleId) {
          await updateBlockedTimeRule({
            availabilityRuleId: blockedTime.ruleId,
            availabilityRule: toUpdateAvailabilityRuleRequest(draft),
          }).unwrap();
          blockedTimeTracking.updateSubmitted({
            blocked_time_id: blockedTime.ruleId,
            duration_minutes: durationMinutes,
            is_all_day: draft.allDay,
            is_recurring: Boolean(draft.rrule),
            scope: draft.scope,
            is_org_schedule: isOrgScheduleActive,
          });
          enqueueSnackbar('Out of office updated', { variant: 'success' });
        } else {
          await createBlockedTimeRule(toCreateAvailabilityRuleRequest(draft)).unwrap();
          blockedTimeTracking.createSubmitted({
            duration_minutes: durationMinutes,
            is_all_day: draft.allDay,
            is_recurring: Boolean(draft.rrule),
            scope: draft.scope,
            is_org_schedule: isOrgScheduleActive,
          });
          enqueueSnackbar('Out of office saved', { variant: 'success' });
        }

        onSaved?.();
        onClose();
      } catch (error) {
        console.error('Failed to save blocked time:', error);
        setError('Unable to save out of office. Please try again.');
      }
    }, [
      blockedTime?.ruleId,
      blockedTimeTracking,
      createBlockedTimeRule,
      draft,
      enqueueSnackbar,
      isEditing,
      isOrgScheduleActive,
      onClose,
      onSaved,
      updateBlockedTimeRule,
      validate,
    ]);

    const handleDelete = useCallback(async () => {
      if (!blockedTime?.ruleId) return;

      blockedTimeTracking.deleteClicked({
        blocked_time_id: blockedTime.ruleId,
        is_recurring: blockedTime.isRecurring,
      });

      try {
        await deleteBlockedTimeRule({ availabilityRuleId: blockedTime.ruleId }).unwrap();
        enqueueSnackbar('Out of office removed', { variant: 'success' });
        onDeleted?.();
        onClose();
      } catch (error) {
        console.error('Failed to delete blocked time:', error);
        setError('Unable to remove out of office. Please try again.');
      }
    }, [
      blockedTime?.isRecurring,
      blockedTime?.ruleId,
      blockedTimeTracking,
      deleteBlockedTimeRule,
      enqueueSnackbar,
      onClose,
      onDeleted,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        remove: handleDelete,
        save: handleSave,
      }),
      [handleDelete, handleSave],
    );

    useEffect(() => {
      onActionStateChange?.({ isEditing, isSubmitting });
    }, [isEditing, isSubmitting, onActionStateChange]);

    return (
      <>
        <Stack spacing={0}>
          <Stack spacing={2} sx={{ px: 3, pt: 2, pb: 2.5 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {overlapsAppointment && (
              <Alert severity="warning">
                This block overlaps existing appointments. Those appointments will stay scheduled.
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {isOrgScheduleActive && (
                <BlockedTimeEditableRow
                  icon={BusinessOutlinedIcon}
                  label="Applies to"
                  value={<PlainValue>{getBlockedTimeScopeLabel(scope)}</PlainValue>}
                >
                  {({ onClose }) => (
                    <BlockedTimeChoiceEditor
                      value={scope}
                      options={[
                        { label: 'Provider', value: 'provider' },
                        { label: 'Entire organization', value: 'organization' },
                      ]}
                      onChange={nextScope => {
                        setScope(nextScope);
                        if (nextScope === 'provider') {
                          setProviderId(providerId ?? initialProviderId ?? defaultProviderId);
                        }
                        onClose();
                      }}
                    />
                  )}
                </BlockedTimeEditableRow>
              )}

              {isOrgScheduleActive && scope === 'provider' && (
                <BlockedTimeEditableRow
                  icon={PersonOutlinedIcon}
                  label="Provider"
                  popoverWidth={280}
                  value={
                    providerName ? (
                      <ProviderValue name={providerName} />
                    ) : (
                      <EmptyValue>Unassigned</EmptyValue>
                    )
                  }
                >
                  {({ onClose }) => (
                    <BlockedTimeProviderEditor
                      value={providerId ?? null}
                      clinicians={clinicians}
                      isLoading={isLoadingClinicians}
                      onChange={nextProviderId => {
                        if (nextProviderId) setProviderId(nextProviderId);
                        onClose();
                      }}
                    />
                  )}
                </BlockedTimeEditableRow>
              )}

              <BlockedTimeEditableRow
                icon={CheckBoxOutlinedIcon}
                label="All day"
                value={<PlainValue>{allDay ? 'Yes' : 'No'}</PlainValue>}
              >
                {({ onClose }) => (
                  <BlockedTimeChoiceEditor
                    value={allDay}
                    options={[
                      { label: 'Yes', value: true },
                      { label: 'No', value: false },
                    ]}
                    onChange={nextAllDay => {
                      setAllDay(nextAllDay);
                      onClose();
                    }}
                  />
                )}
              </BlockedTimeEditableRow>

              <BlockedTimeEditableRow
                icon={EventOutlinedIcon}
                label="Date/time"
                popoverWidth={280}
                value={<PlainValue>{dateTimeLabel}</PlainValue>}
              >
                {() => (
                  <BlockedTimeDateTimeEditor
                    allDay={allDay}
                    date={date}
                    startTime={startTime}
                    onDateChange={handleStartDateChange}
                    onStartTimeChange={handleStartTimeChange}
                  />
                )}
              </BlockedTimeEditableRow>

              <BlockedTimeEditableRow
                icon={TimerOutlinedIcon}
                label="Duration"
                popoverWidth={420}
                value={<PlainValue>{durationLabel}</PlainValue>}
              >
                {() => (
                  <BlockedTimeDurationEditor
                    allDay={allDay}
                    date={date}
                    startTime={startTime}
                    endDate={endDate}
                    endTime={endTime}
                    onEndDateChange={setEndDate}
                    onEndTimeChange={setEndTime}
                    onDurationMinutesChange={handleTimedDurationChange}
                  />
                )}
              </BlockedTimeEditableRow>

              <BlockedTimeEditableRow
                icon={RepeatOutlinedIcon}
                label="Recurrence"
                popoverWidth={300}
                alignTop={recurrence.recurrence === 'custom'}
                value={<PlainValue>{recurrenceLabel}</PlainValue>}
              >
                {({ onClose }) => (
                  <BlockedTimeRecurrenceEditor
                    value={recurrence.recurrence}
                    endDate={recurrence.recurrenceEndDate}
                    recurrenceText={recurrence.getRecurrenceText()}
                    onRecurrenceChange={nextRecurrence => {
                      if (nextRecurrence === 'custom') {
                        recurrence.handleRecurrenceChange(nextRecurrence);
                        onClose();
                        return;
                      }

                      recurrence.handleRecurrenceChange(nextRecurrence);
                    }}
                    onEndDateChange={nextEndDate => {
                      recurrence.setRecurrenceEndDate(nextEndDate);
                      recurrence.setHasRecurrenceEnd(Boolean(nextEndDate));
                    }}
                    onEditCustom={() => {
                      recurrence.setIsCustomRecurrenceModalOpen(true);
                      onClose();
                    }}
                  />
                )}
              </BlockedTimeEditableRow>
            </Box>
          </Stack>

          {!hideActions && (
            <Stack
              direction="row"
              spacing={1.5}
              sx={{
                px: 2,
                py: 2,
                borderTop: `1px solid ${ELIG_TOKENS.line}`,
                bgcolor: 'background.paper',
                boxShadow: '0 -8px 24px rgba(28, 30, 133, 0.06)',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                {isEditing && (
                  <Button
                    color="error"
                    startIcon={<DeleteOutlineOutlinedIcon />}
                    onClick={handleDelete}
                    disabled={isSubmitting}
                  >
                    Remove out of office
                  </Button>
                )}
              </Box>
              <Stack direction="row" spacing={1.5}>
                <Button onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>

        <CustomRecurrenceModal
          open={recurrence.isCustomRecurrenceModalOpen}
          onClose={() => recurrence.setIsCustomRecurrenceModalOpen(false)}
          onSave={recurrence.handleCustomRecurrenceSave}
          initialRule={recurrence.customRecurrenceRule || undefined}
        />
      </>
    );
  },
);

BlockedTimeForm.displayName = 'BlockedTimeForm';

const BlockedTimeEditableRow = ({
  icon,
  label,
  value,
  children,
  alignTop = false,
  popoverWidth = 260,
}: {
  icon: ComponentType<SvgIconProps>;
  label: string;
  value: React.ReactNode;
  children: (props: { onClose: () => void }) => React.ReactNode;
  alignTop?: boolean;
  popoverWidth?: number;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useDismiss(context),
    useRole(context, { role: 'dialog' }),
  ]);

  return (
    <>
      <BlockedTimePropertyRow
        icon={icon}
        label={label}
        active={isOpen}
        alignTop={alignTop}
        onClick={() => setIsOpen(open => !open)}
        referenceRef={refs.setReference}
        interactionProps={getReferenceProps()}
      >
        {value}
      </BlockedTimePropertyRow>
      {isOpen && (
        <FloatingPortal>
          <Box
            {...getFloatingProps()}
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: 1500 }}
            sx={{
              width: popoverWidth,
              maxWidth: 'calc(100vw - 32px)',
              borderRadius: 2,
              border: `1px solid ${ELIG_TOKENS.lineStrong}`,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              bgcolor: ELIG_TOKENS.card,
              overflow: 'hidden',
            }}
          >
            {children({ onClose: () => setIsOpen(false) })}
          </Box>
        </FloatingPortal>
      )}
    </>
  );
};

const BlockedTimePropertyRow = ({
  icon: Icon,
  label,
  children,
  alignTop = false,
  active = false,
  onClick,
  referenceRef,
  interactionProps,
}: {
  icon: ComponentType<SvgIconProps>;
  label: string;
  children: React.ReactNode;
  alignTop?: boolean;
  active?: boolean;
  onClick?: React.MouseEventHandler<HTMLElement>;
  referenceRef?: (node: HTMLElement | null) => void;
  interactionProps?: React.HTMLAttributes<HTMLElement>;
}) => {
  const content = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: ELIG_TOKENS.inkSoft }}>
        <Icon sx={{ fontSize: 14, mt: alignTop ? 0.25 : 0 }} />
        <Typography
          variant="body2"
          sx={{ fontSize: 12, fontWeight: 500, color: ELIG_TOKENS.inkSoft }}
        >
          {label}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </>
  );

  return (
    <Box
      {...interactionProps}
      ref={referenceRef}
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '104px minmax(0, 1fr)', sm: '120px minmax(0, 1fr)' },
        alignItems: alignTop ? 'flex-start' : 'center',
        gap: 1,
        py: 0.75,
        px: 0.5,
        border: 0,
        borderRadius: 1,
        bgcolor: active ? 'rgba(0,0,0,0.04)' : 'transparent',
        color: 'inherit',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'background-color 0.12s',
        width: '100%',
        '&:hover': { bgcolor: active ? 'rgba(0,0,0,0.04)' : 'rgba(0,0,0,0.025)' },
      }}
    >
      {content}
    </Box>
  );
};

const BlockedTimeChoiceEditor = <T extends string | boolean>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{ label: string; value: T }>;
  onChange: (value: T) => void;
}) => (
  <Stack sx={{ py: 0.5 }}>
    {options.map(option => (
      <BlockedTimeOptionRow
        key={String(option.value)}
        selected={option.value === value}
        onClick={() => onChange(option.value)}
      >
        <PlainValue>{option.label}</PlainValue>
      </BlockedTimeOptionRow>
    ))}
  </Stack>
);

const BlockedTimeProviderEditor = ({
  value,
  clinicians,
  isLoading,
  onChange,
}: {
  value: string | null;
  clinicians: Array<{ userId: string; name: string }>;
  isLoading: boolean;
  onChange: (providerId: string | null) => void;
}) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1 }}>
        <CircularProgress size={14} />
        <EmptyValue>Loading...</EmptyValue>
      </Box>
    );
  }

  if (clinicians.length === 0) {
    return (
      <Box sx={{ px: 1.5, py: 1 }}>
        <EmptyValue>No providers available</EmptyValue>
      </Box>
    );
  }

  return (
    <Stack sx={{ py: 0.5 }}>
      {clinicians.map(clinician => (
        <BlockedTimeOptionRow
          key={clinician.userId}
          selected={clinician.userId === value}
          onClick={() => onChange(clinician.userId)}
        >
          <ProviderValue name={clinician.name} />
        </BlockedTimeOptionRow>
      ))}
    </Stack>
  );
};

const BlockedTimeDateTimeEditor = ({
  allDay,
  date,
  startTime,
  onDateChange,
  onStartTimeChange,
}: {
  allDay: boolean;
  date: string;
  startTime: string;
  onDateChange: (value: string) => void;
  onStartTimeChange: (value: string) => void;
}) => (
  <Stack spacing={1.25} sx={{ p: 1.5 }} onClick={event => event.stopPropagation()}>
    <TextField
      autoFocus
      label="Date"
      type="date"
      size="small"
      value={date}
      onChange={event => onDateChange(event.target.value)}
      fullWidth
      slotProps={{ inputLabel: { shrink: true } }}
    />
    {!allDay && (
      <TextField
        label="Time"
        type="time"
        size="small"
        value={startTime}
        onChange={event => onStartTimeChange(event.target.value)}
        fullWidth
        slotProps={{ inputLabel: { shrink: true } }}
      />
    )}
  </Stack>
);

const BlockedTimeDurationEditor = ({
  allDay,
  date,
  startTime,
  endDate,
  endTime,
  onEndDateChange,
  onEndTimeChange,
  onDurationMinutesChange,
}: {
  allDay: boolean;
  date: string;
  startTime: string;
  endDate: string;
  endTime: string;
  onEndDateChange: (value: string) => void;
  onEndTimeChange: (value: string) => void;
  onDurationMinutesChange: (minutes: number) => void;
}) => (
  <Stack spacing={1.25} sx={{ p: 1.5 }} onClick={event => event.stopPropagation()}>
    {allDay ? (
      <TextField
        autoFocus
        label="End date"
        type="date"
        size="small"
        value={endDate}
        onChange={event => onEndDateChange(event.target.value)}
        fullWidth
        slotProps={{ inputLabel: { shrink: true } }}
      />
    ) : (
      <>
        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
          {DURATION_PRESETS.map(duration => (
            <Button
              key={duration}
              size="small"
              variant={
                isDurationPresetSelected(date, startTime, endDate, endTime, duration)
                  ? 'contained'
                  : 'outlined'
              }
              onClick={() => onDurationMinutesChange(duration)}
              sx={{ minWidth: 0, textTransform: 'none' }}
            >
              {formatDurationMinutes(duration)}
            </Button>
          ))}
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
          <TextField
            label="End date"
            type="date"
            size="small"
            value={endDate}
            onChange={event => onEndDateChange(event.target.value)}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="End time"
            type="time"
            size="small"
            value={endTime}
            onChange={event => onEndTimeChange(event.target.value)}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Box>
      </>
    )}
  </Stack>
);

const BlockedTimeRecurrenceEditor = ({
  value,
  endDate,
  recurrenceText,
  onRecurrenceChange,
  onEndDateChange,
  onEditCustom,
}: {
  value: string;
  endDate: string;
  recurrenceText: string;
  onRecurrenceChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  onEditCustom: () => void;
}) => (
  <Stack spacing={0.5} sx={{ py: 0.5 }}>
    {RECURRENCE_OPTIONS.map(option => (
      <BlockedTimeOptionRow
        key={option.value}
        selected={option.value === value}
        onClick={() => onRecurrenceChange(option.value)}
      >
        <PlainValue>{option.value === 'none' ? 'One-time' : option.label}</PlainValue>
      </BlockedTimeOptionRow>
    ))}

    {value !== 'none' && value !== 'custom' && (
      <Box sx={{ px: 1.5, pb: 1, pt: 0.5 }} onClick={event => event.stopPropagation()}>
        <TextField
          label="Ends on"
          type="date"
          size="small"
          value={endDate}
          onChange={event => onEndDateChange(event.target.value)}
          fullWidth
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Box>
    )}

    {value === 'custom' && (
      <Box sx={{ px: 1.5, pb: 1, pt: 0.5 }}>
        <Typography
          variant="body2"
          sx={{ fontSize: 13, color: ELIG_TOKENS.inkSoft, lineHeight: 1.4 }}
        >
          {recurrenceText}
        </Typography>
        <Button size="small" onClick={onEditCustom} sx={{ mt: 0.5, textTransform: 'none' }}>
          Edit recurrence
        </Button>
      </Box>
    )}
  </Stack>
);

const BlockedTimeOptionRow = ({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <ButtonBase
    onClick={event => {
      event.stopPropagation();
      onClick();
    }}
    sx={{
      width: '100%',
      justifyContent: 'flex-start',
      gap: 1,
      px: 1.5,
      py: 0.75,
      color: ELIG_TOKENS.ink,
      textAlign: 'left',
      bgcolor: selected ? 'rgba(28, 30, 133, 0.06)' : 'transparent',
      '&:hover': { bgcolor: selected ? 'rgba(28, 30, 133, 0.08)' : 'rgba(0,0,0,0.035)' },
    }}
  >
    {children}
  </ButtonBase>
);

const ProviderValue = ({ name }: { name: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
    <ProviderBadge name={name} />
    <PlainValue>{name}</PlainValue>
  </Box>
);

const PlainValue = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="body2"
    sx={{
      fontSize: 13,
      color: ELIG_TOKENS.ink,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Typography>
);

const EmptyValue = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" sx={{ fontSize: 13, color: ELIG_TOKENS.inkFaint }}>
    {children}
  </Typography>
);

const getBlockedTimeScopeLabel = (scope: BlockedTimeScope): string =>
  scope === 'organization' ? 'Entire organization' : 'Provider';

const formatBlockedTimeStartDateTime = ({
  allDay,
  date,
  startTime,
}: {
  allDay: boolean;
  date: string;
  startTime: string;
}): string => {
  if (!date) return 'Set date/time...';

  const startDateLabel = formatDateInput(date, 'EEE MMM d');

  if (allDay) return startDateLabel;

  const startTimeLabel = formatTimeInput(startTime);

  return `${startDateLabel} · ${startTimeLabel}`;
};

const formatBlockedTimeDuration = ({
  allDay,
  date,
  endDate,
  draft,
}: {
  allDay: boolean;
  date: string;
  endDate: string;
  draft: ReturnType<typeof buildBlockedTimeDraft>;
}): string => {
  if (allDay) {
    const days = getInclusiveDateSpanDays(date, endDate);
    return days === 1 ? 'All day' : `${days} days`;
  }

  if (!draft) return 'Set duration...';

  return formatDurationMinutes(coercePositiveDurationMinutes(draft.start, draft.end));
};

const getBlockedTimeRecurrenceLabel = ({
  recurrence,
  recurrenceText,
  recurrenceEndDate,
}: {
  recurrence: string;
  recurrenceText: string;
  recurrenceEndDate: string;
}): string => {
  if (recurrence === 'none') return 'One-time';
  if (recurrence === 'custom') return recurrenceText || 'Custom recurrence';

  const option = RECURRENCE_OPTIONS.find(item => item.value === recurrence);
  const label = option?.label ?? 'Custom recurrence';
  const endDateLabel = recurrenceEndDate ? formatDateInput(recurrenceEndDate, 'MMM d, yyyy') : null;

  return endDateLabel ? `${label} · Ends ${endDateLabel}` : label;
};

const formatDateInput = (value: string, formatString: string): string => {
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, formatString) : value;
};

const formatTimeInput = (value: string): string => {
  const [hoursValue, minutesValue] = value.split(':');
  const hours = Number(hoursValue);
  const minutes = Number(minutesValue);

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return value || '--:--';

  return format(new Date(2000, 0, 1, hours, minutes), 'h:mm a');
};

const formatDurationMinutes = (totalMinutes: number): string => {
  if (totalMinutes < 24 * 60) return `${totalMinutes} min`;

  const days = Math.floor(totalMinutes / (24 * 60));
  const remainderMinutes = totalMinutes % (24 * 60);
  const hours = Math.floor(remainderMinutes / 60);
  const minutes = remainderMinutes % 60;
  const parts = [`${days} ${days === 1 ? 'day' : 'days'}`];

  if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hr' : 'hrs'}`);
  if (minutes > 0) parts.push(`${minutes} min`);

  return parts.join(' ');
};

const getInclusiveDateSpanDays = (date: string, endDate: string): number => {
  const start = parseISO(date);
  const end = parseISO(endDate || date);

  if (!isValid(start) || !isValid(end)) return 1;

  return Math.max(1, differenceInCalendarDays(end, start) + 1);
};

const addDaysToDateInput = (date: string, days: number): string => {
  const parsed = parseISO(date);

  if (!isValid(parsed)) return date;

  return toDateInputValue(add(parsed, { days }));
};

const parseLocalDateTimeInput = (date: string, time: string): Date | null => {
  if (!date || !time) return null;

  const parsed = new Date(`${date}T${time}`);

  return isValid(parsed) ? parsed : null;
};

const getValidTimedDurationMinutes = (
  date: string,
  startTime: string,
  endDate: string,
  endTime: string,
): number | null => {
  const start = parseLocalDateTimeInput(date, startTime);
  const end = parseLocalDateTimeInput(endDate, endTime);

  if (!start || !end) return null;

  const durationMinutes = differenceInMinutes(end, start);

  return durationMinutes > 0 ? durationMinutes : null;
};

const getTimedDurationMinutes = (
  date: string,
  startTime: string,
  endDate: string,
  endTime: string,
): number => getValidTimedDurationMinutes(date, startTime, endDate, endTime) ?? 60;

const setEndFromDuration = (
  date: string,
  startTime: string,
  durationMinutes: number,
  setEndDate: (value: string) => void,
  setEndTime: (value: string) => void,
) => {
  const start = parseLocalDateTimeInput(date, startTime);

  if (!start) return;

  const end = add(start, { minutes: durationMinutes });

  setEndDate(toDateInputValue(end));
  setEndTime(toTimeInputValue(end));
};

const isDurationPresetSelected = (
  date: string,
  startTime: string,
  endDate: string,
  endTime: string,
  duration: number,
): boolean => getValidTimedDurationMinutes(date, startTime, endDate, endTime) === duration;

export default BlockedTimeForm;
